import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { useNotification } from '../components/Notification';
import { formatUsdAmount } from '../utils/money';
import { ensureGasOrDrip } from '../utils/gasDrip';
import { withdrawJackpotWithAuth } from '../utils/blockchain';
import { linkWalletWithProof, isWalletLimitError } from '../utils/walletAuth';

const Jackpot = () => {
  const [searchParams] = useSearchParams();
  const cupSlug = searchParams.get('cup');
  const { user } = useAuth();
  const { account, ensureConnected } = useWallet();
  const { showNotification } = useNotification();
  const [jackpots, setJackpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimSummary, setClaimSummary] = useState(null);
  const [claimingAll, setClaimingAll] = useState(false);
  const filter = 'free';
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });

  const fetchClaimSummary = useCallback(async () => {
    if (!user) {
      setClaimSummary(null);
      return;
    }
    try {
      const { data } = await api.get('/jackpots/claim-all/summary');
      setClaimSummary(data);
    } catch {
      setClaimSummary(null);
    }
  }, [user]);

  const fetchJackpots = useCallback(async () => {
    setLoading(true);
    try {
      const page = pagination.currentPage || 1;
      const endpoint = `/jackpots/items?type=${filter}&page=${page}`;
      const response = await api.get(endpoint);
      if (response.data.items) {
        setJackpots(response.data.items || []);
        setPagination((prev) => response.data.pagination || prev);
      } else {
        setJackpots(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching jackpots:', error);
      setJackpots([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage]);

  useEffect(() => {
    fetchJackpots();
  }, [cupSlug, fetchJackpots]);

  useEffect(() => {
    fetchClaimSummary();
  }, [fetchClaimSummary]);

  const handleClaimAll = async () => {
    if (claimingAll || !claimSummary?.canClaimAll) return;
    setClaimingAll(true);
    let walletAddress = account;
    try {
      if (!walletAddress) {
        try {
          walletAddress = await ensureConnected();
        } catch {
          showNotification('Connect your wallet (same as on your profile)', 'warning');
          return;
        }
      }
      if (!walletAddress) {
        showNotification('Connect your wallet (same as on your profile)', 'warning');
        return;
      }

      try {
        await linkWalletWithProof(walletAddress);
      } catch (e) {
        if (isWalletLimitError(e)) return;
        showNotification(e?.response?.data?.message || 'Unable to link wallet', 'error');
        return;
      }

      const { data: auth } = await api.post('/jackpots/claim-all/authorization', {
        walletAddress,
      });

      const okGas = await ensureGasOrDrip(walletAddress, {
        label: 'jackpot claim',
        playType: 'free',
        showNotification,
        claimAmountUsdc: auth.amountUsdc,
      });
      if (!okGas) return;

      const txHash = await withdrawJackpotWithAuth(
        auth.amountWei,
        auth.nonce,
        auth.deadline,
        auth.signature
      );
      showNotification(`Claim-all sent! TX: ${String(txHash).slice(0, 10)}...`, 'success');

      try {
        await api.post('/transactions', {
          action: 'jackpot_withdraw',
          txHash,
          amount: auth.amountUsdc,
          currency: 'USDC',
          meta: { batch: true, winCount: auth.winCount, predictionIds: auth.predictionIds },
        });
      } catch {
        /* ignore */
      }

      let lastErr;
      for (let i = 0; i < 4; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { data } = await api.post('/jackpots/claim-all/confirm', {
            txHash,
            walletAddress,
            predictionIds: auth.predictionIds,
            amountUsdc: auth.amountUsdc,
          });
          showNotification(
            `Claimed ${formatUsdAmount(data.withdrawn || auth.amountUsdc)} from ${data.claimedCount || auth.winCount} win(s)`,
            'success'
          );
          await fetchClaimSummary();
          return;
        } catch (e) {
          lastErr = e;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 1200));
        }
      }
      throw lastErr || new Error('Confirm failed');
    } catch (error) {
      console.error('claim-all:', error);
      showNotification(error?.response?.data?.message || error.message || 'Claim failed', 'error');
      await fetchClaimSummary();
    } finally {
      setClaimingAll(false);
    }
  };

  if (loading && !jackpots.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Jackpots</h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Claim all free-jackpot wins in one transaction, or browse events below.
          </p>
        </div>

        {user ? (
          <div className="mb-8 rounded-xl border border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-800 p-5 sm:p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">Your free jackpot</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  One claim covers every unclaimed win — saves gas vs claiming per game.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClaimAll}
                disabled={claimingAll || !claimSummary?.canClaimAll}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold shadow-sm"
              >
                {claimingAll ? (
                  <>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Claiming…
                  </>
                ) : (
                  `Claim all ${formatUsdAmount(claimSummary?.unclaimedUsdc || 0)}`
                )}
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Unclaimed</div>
                <div className="font-bold tabular-nums text-amber-700 dark:text-amber-300">
                  {formatUsdAmount(claimSummary?.unclaimedUsdc || 0)}
                </div>
                <div className="text-[11px] text-gray-400">
                  {claimSummary?.unclaimedGames || 0} game(s)
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Claimed</div>
                <div className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">
                  {formatUsdAmount(claimSummary?.claimedUsdc || 0)}
                </div>
                <div className="text-[11px] text-gray-400">
                  {claimSummary?.claimedGames || 0} game(s)
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Games won</div>
                <div className="font-bold tabular-nums text-gray-900 dark:text-white">
                  {claimSummary?.gamesWonTotal || 0}
                </div>
              </div>
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2">
                <div className="text-xs text-gray-500 dark:text-gray-400">Balance ready</div>
                <div className="font-bold tabular-nums text-purple-700 dark:text-purple-300">
                  {formatUsdAmount(claimSummary?.jackpotBalance || 0)}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
            Log in to see your claimable free jackpot and claim all wins at once.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {jackpots.map((jackpot) => (
            <div
              key={jackpot._id}
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-2 ${
                jackpot.status === 'resolved'
                  ? 'border-green-500 dark:border-green-600'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-xs font-semibold rounded">
                  Free Jackpot
                </span>
                <span
                  className={`px-2 py-1 text-xs font-semibold rounded ${
                    jackpot.status === 'resolved'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                  }`}
                >
                  {jackpot.status === 'resolved' ? 'Resolved' : 'Pending'}
                </span>
              </div>

              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {jackpot.title}
              </h3>

              {jackpot.cup && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{jackpot.cup.name}</p>
              )}

              <div className="mb-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Pool Amount</div>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {formatUsdAmount(jackpot.amount)}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Total Tickets</span>
                  <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
                    {jackpot.totalTickets ?? 0}
                  </span>
                </div>
                {user && (jackpot.userTickets ?? 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Your Tickets</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400 tabular-nums">
                      {jackpot.userTickets}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Participants</span>
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {jackpot.participants || 0}
                  </span>
                </div>
              </div>

              <a
                href={
                  jackpot.itemType === 'match'
                    ? `/match/${jackpot.itemId}/free`
                    : `/poll/${jackpot.itemId}/free`
                }
                className="mt-4 block w-full text-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
              >
                View Event
              </a>
            </div>
          ))}
        </div>

        {jackpots.length === 0 && (
          <div className="text-center py-12 text-gray-600 dark:text-gray-400">No jackpots found</div>
        )}

        {pagination.totalPages > 1 && (
          <div className="mt-8 flex justify-center gap-4">
            <button
              type="button"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: Math.max(1, (prev.currentPage || 1) - 1),
                }))
              }
              disabled={!pagination.hasPrev}
              className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-gray-700 dark:text-gray-300">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              type="button"
              onClick={() =>
                setPagination((prev) => ({
                  ...prev,
                  currentPage: Math.min(prev.totalPages, (prev.currentPage || 1) + 1),
                }))
              }
              disabled={!pagination.hasNext}
              className="px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-700 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Jackpot;
